import { Injectable } from '@angular/core'
import { Subject } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { ApiService } from '@app/core/graphql/services/api.service'
import { AuthService } from '@app/core/auth/services/auth.service'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { MessageService } from 'primeng/api'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { FIND_APPROVED_LOGS, GET_REQUIREMENT_SUBMISSIONS, UPDATE_REQUIREMENT_SUBMISSION } from '@app/query/requirement_submission'
import { RequirementSubmission, UpdateRequirementSubmissionInput } from '@app/models/requirement_submission'
import { RequirementStatus } from '@app/shared/app.enums'

@Injectable({
  providedIn: 'root',
})
export class RequirementSubmissionsService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'REQUIREMENT_SUBMISSIONS'
  sysMessage: any
  constructor(private API: ApiService, private translateMessagesService: TranslateMessagesService, private messageService: MessageService, private authService: AuthService) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  get(pageOptions?: any) {
    const response = this.API.privateQuery<any>(GET_REQUIREMENT_SUBMISSIONS, { status: RequirementStatus.IN_REVIEW, pageOptions }).pipe(
      map((response: any) => {
        if (response.success) {
          const submissions = response.data as RequirementSubmission[]
          // Filter submissions based on user's assigned requirements
          const filteredSubmissions = this.filterSubmissionsByUserRequirements(submissions)
          return {
            data: filteredSubmissions,
            pagination: response.pagination,
          }
        } else {
          return {
            data: [] as RequirementSubmission[],
            pagination: null,
          }
        }
      }),
      tap((result: any) => {
        this.subscriber.next({
          type: `GET_${this.id}`,
          data: result.data,
        })
      })
    )

    return response
  }

  getArchived(filters?: any, pageOptions?: any) {
    const response = this.API.privateQuery<any>(FIND_APPROVED_LOGS, { filters, pageOptions }).pipe(
      map((response: any) => {
        if (response.success) {
          const submissions = response.data as RequirementSubmission[]
          // Filter submissions based on user's assigned requirements
          const filteredSubmissions = this.filterSubmissionsByUserRequirements(submissions)
          return {
            data: filteredSubmissions,
            pagination: response.pagination,
          }
        } else {
          return {
            data: [] as RequirementSubmission[],
            pagination: null,
          }
        }
      }),
      tap((result: any) => {
        this.subscriber.next({
          type: `GET_${this.id}`,
          data: result.data,
        })
      })
    )

    return response
  }

  update(payload: UpdateRequirementSubmissionInput, silence?: boolean) {
    return this.API.privateMutation<any>(UPDATE_REQUIREMENT_SUBMISSION, { data: payload }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS_NO_ENTITY_NAME'),
          })

          /**
           * YB-2095 / YB-2124 — the submission SUCCEEDED but the vehicle it describes was not
           * created: a full licence, or a plate already registered for these dates.
           *
           * These sentences name the client's licence position, so they are addressed to whoever
           * is watching here and are never relayed to the person who filled the form. Shown as a
           * warning of its own rather than folded into the success toast: approving an edit that
           * mints nothing leaves the holder pointing at a vehicle that has no pass, and there is
           * no other way to find that out.
           */
          for (const warning of data.asset_pass_warnings ?? []) {
            this.messageService.add({
              severity: 'warn',
              icon: 'pi pi-exclamation-triangle',
              summary: this.sysMessage.message('UPDATE_SUCCESS_NO_ENTITY_NAME').summary,
              detail: warning,
              life: 10000,
            })
          }

          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data[0] as RequirementSubmission,
          })
        }
      })
    )
  }

  /**
   * Filter requirement submissions based on user's assigned requirements.
   * If user has no assigned requirements, return all submissions.
   * If user has assigned requirements, only return submissions that contain at least one requirement the user can approve.
   */
  private filterSubmissionsByUserRequirements(submissions: RequirementSubmission[]): RequirementSubmission[] {
    const currentUser = this.authService.user

    // If user has no assigned requirements or is admin, show all submissions
    if (!currentUser?.approver_requirement_ids || currentUser.approver_requirement_ids.length === 0) {
      return submissions
    }

    // Filter submissions to only include those with requirements the user can approve
    return submissions.filter((submission) => {
      // Check if this submission has any requirements that the user is assigned to approve
      return submission.requirements?.some((requirement) => currentUser.approver_requirement_ids!.includes(requirement.id))
    })
  }
}
